import ButtonBasic from "@rentiohq/shared-frontend/dist/components/components/ButtonBasic";
import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import { CONFIG } from "@rentiohq/shared-frontend/dist/config/app.config";
import { logError } from "@rentiohq/shared-frontend/dist/logger/logger";
import {
  Card,
  DisplayText,
  Page,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import {
  useInternalMode,
  useLocaleAsKeys,
} from "@rentiohq/web-shared/dist/redux/system/system.hooks";
import React from "react";

interface IHealthV1 {
  buildNumber: string;
  commitHash: string;
  env: string;
}

interface IHealthV2 {
  data: {
    status: string;
    time: string;
    info: {
      buildNumber: string;
      commitHash: string;
      env: string;
      logLevel: string;
    };
  };
}

const useApiHealth = <T,>(url: string) => {
  const [data, setData] = React.useState<T | undefined>();
  const [error, setError] = React.useState<string | undefined>();
  const [loading, setLoading] = React.useState(true);

  const fetchHealth = React.useCallback(() => {
    setLoading(true);
    setError(undefined);

    fetch(url)
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then(json => {
        setData(json);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [url]);

  React.useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  return { data, error, loading, refetch: fetchHealth };
};

const ApiHealthCard = <T,>({
  title,
  url,
  renderData,
}: {
  title: string;
  url: string;
  renderData: (data: T) => React.ReactNode;
}) => {
  const { data, error, loading, refetch } = useApiHealth<T>(url);

  return (
    <Card>
      <DisplayText>{title}</DisplayText>
      <div>
        <TextStyle variation="subdued">{url}</TextStyle>
      </div>
      <Spacer weight={ESpacerWeight.W08} />
      {loading && (
        <div>
          <TextStyle>Loading...</TextStyle>
        </div>
      )}
      {error && (
        <div>
          <TextStyle variation="negative">Error: {error}</TextStyle>
        </div>
      )}
      {data && renderData(data)}
      <Spacer weight={ESpacerWeight.W08} />
      <ButtonBasic title="Refresh" onPress={refetch} />
    </Card>
  );
};

export const RentioInternal = () => {
  // Redux
  const { internalModeEnabled, toggleInternalMode } = useInternalMode();
  const { localeAsKeysEnabled, toggleLocaleAsKeys } = useLocaleAsKeys();

  // Render
  return (
    <Page
      title={"Rentio Internal"}
      breadcrumbs={{
        content: "Back to dashboard",
        to: "/",
      }}
    >
      <Spacer weight={ESpacerWeight.W16} />

      <Card>
        <DisplayText>Version</DisplayText>
        <TextStyle>
          {CONFIG.SENTRY_RELEASE || "Release version not set"}
        </TextStyle>
      </Card>

      <Spacer weight={ESpacerWeight.W16} />

      <Card>
        <DisplayText>Deploy date</DisplayText>
        <TextStyle>
          {CONFIG.DEPLOY_DATE ? `${CONFIG.DEPLOY_DATE}` : "Deploy date not set"}
        </TextStyle>
      </Card>

      <Spacer weight={ESpacerWeight.W16} />

      <Card>
        <DisplayText>
          Internal mode enabled: {internalModeEnabled ? "yes" : "no"}
        </DisplayText>

        <ButtonBasic
          title="Toggle internal mode"
          onPress={toggleInternalMode}
        />
      </Card>

      <ApiHealthCard
        title="API V2"
        url={`${CONFIG.BASE_URL_V2}/health`}
        renderData={(data: IHealthV2) => (
          <>
            <div>
              <TextStyle>
                Status:{" "}
                <TextStyle variation="strong">{data.data?.status}</TextStyle>
              </TextStyle>
            </div>
            <div>
              <TextStyle>Build: {data.data?.info?.buildNumber}</TextStyle>
            </div>
            <div>
              <TextStyle>Commit: {data.data?.info?.commitHash}</TextStyle>
            </div>
            <div>
              <TextStyle>Env: {data.data?.info?.env}</TextStyle>
            </div>
            <div>
              <TextStyle>Log level: {data.data?.info?.logLevel}</TextStyle>
            </div>
            <div>
              <TextStyle>
                Time:{" "}
                {data.data?.time
                  ? new Date(data.data.time).toLocaleString()
                  : "-"}
              </TextStyle>
            </div>
          </>
        )}
      />

      <Spacer weight={ESpacerWeight.W16} />

      <ApiHealthCard
        title="API V1"
        url={`${CONFIG.BASE_URL_V1}/health`}
        renderData={(data: IHealthV1) => (
          <>
            <div>
              <TextStyle>Build: {data.buildNumber}</TextStyle>
            </div>
            <div>
              <TextStyle>Commit: {data.commitHash}</TextStyle>
            </div>
            <div>
              <TextStyle>Env: {data.env}</TextStyle>
            </div>
          </>
        )}
      />

      <Spacer weight={ESpacerWeight.W16} />

      <Card>
        <DisplayText>
          Locale as keys: {localeAsKeysEnabled ? "yes" : "no"}
        </DisplayText>

        <ButtonBasic
          title="Toggle locale as keys"
          onPress={toggleLocaleAsKeys}
        />
      </Card>

      <Card>
        <DisplayText>Crash</DisplayText>

        <ButtonBasic
          title="Simulate crash"
          onPress={() => {
            logError({
              error: new Error("rentio-internal-crash"),
              fatal: true,
            });
          }}
        />
      </Card>
    </Page>
  );
};

// eslint-disable-next-line import/no-default-export
export default RentioInternal;
